import {
  AcademicCapIcon,
  BellIcon,
  ClipboardCheckIcon,
  CursorClickIcon,
  CurrencyDollarIcon,
  DesktopComputerIcon,
  HeartIcon,
  HomeIcon,
  LibraryIcon,
  PencilAltIcon,
  PresentationChartLineIcon,
  UserCircleIcon,
  UsersIcon,
} from "@heroicons/vue/outline"
import User from "../types/user"
import type { NavItem } from "@xy-planning-network/trees"
import { staffBaseNavigation } from "@/composables/staff/navigation"
import { router } from "@/routes"
import type { UserMenuItem } from "@/types/navigation"
import { computed } from "vue"
import useAppTodosCount from "@/composables/useAppTodosCount"
import useAppMessagesCount from "@/composables/useAppMessagesCount"
import { getAdvSeasonStatus } from "@/composables/benefits"

/**
 * Get the base navigation for the sidebar with user context
 * @param user The current user
 * @returns Array<NavItem> an array of navigation items
 */
export function getBaseNavigation(user: User): Array<NavItem> {
  // NOTE(spk): The community search and profiles feature is accessible to members and staff.
  // Those pages are wired up with the AppLayout which references this function for building
  // navigation.
  if (user.isStaff) {
    return staffBaseNavigation
  }

  // NOTE(spk): users who have not completed the new-application do not see the base navigation.
  // However there are pages such as /settings and /support they can reach where the sidebar base navigation will be displayed.
  if (!user.completedNewApp) {
    return [
      {
        icon: PencilAltIcon,
        name: "Membership Application",
        openInTab: false,
        url: router.NewApplication(),
      },
      {
        icon: HeartIcon,
        name: "Help & Support",
        openInTab: false,
        url: router.Support(),
      },
    ]
  }

  // NOTE(spk): users who have not completed the account-setup do not see the base navigation.
  // However there are pages such as /settings and /support they can reach where the sidebar base navigation will be displayed.
  if (!user.completedAccountSetup) {
    return [
      {
        icon: ClipboardCheckIcon,
        name: "Account Setup",
        openInTab: false,
        url: router.AccountSetup(),
      },
      {
        icon: HeartIcon,
        name: "Help & Support",
        openInTab: false,
        url: router.Support(),
      },
    ]
  }

  /**
   * Directory of all navigation items that can appear in the sidebar nav.
   * The display method determines if it should be displayed to the given user.
   * The order is determined by the property order of this object.
   */
  const { inAdvSeason } = getAdvSeasonStatus()
  return [
    {
      icon: HomeIcon,
      name: "Dashboard",
      openInTab: false,
      url: "/dashboard",
      display: user.isXypnMember,
    },
    {
      icon: DesktopComputerIcon,
      name: "Member Benefits",
      openInTab: false,
      url: "/benefits",
      display: user.canAccessMemberContent && user.canManage,
    },
    {
      icon: UsersIcon,
      name: "Community",
      openInTab: false,
      url: "/community",
      display: user.canAccessMemberContent,
    },
    {
      icon: AcademicCapIcon,
      name: "XYPN Academy",
      openInTab: true,
      url: "/sso/saml2/academy",
      display: user.canAccessMemberContent,
    },
    {
      icon: LibraryIcon,
      name: "My Firm",
      openInTab: false,
      url: "/users",
      display:
        user.canAccessMemberContent && user.canManage && !user.firm?.isSapphire,
    },
    {
      icon: CurrencyDollarIcon,
      name: "Billing",
      openInTab: false,
      url: "/billing",
      display: user.canAccessMemberContent && user.isFirmOwner,
    },
    {
      icon: PresentationChartLineIcon,
      name: "Benchmarking",
      openInTab: false,
      url: "/benchmarking",
      display: user.canAccessMemberContent && !user.firm?.isSapphire,
    },
    {
      icon: ClipboardCheckIcon,
      name: "To-Dos",
      openInTab: false,
      url: "/todos",
      display: user.canAccessMemberContent,
    },
    // NOTE(spk): The ADV Update Guide can be hidden from the nav after April 1st, 2025
    {
      icon: CursorClickIcon,
      name: "Annual ADV Update",
      openInTab: false,
      url: router.AnnualAdvUpdate(),
      display: inAdvSeason && !user.firm?.isSapphire,
    },
    {
      icon: HeartIcon,
      name: "Help & Support",
      openInTab: false,
      url: router.Support(),
      display: true,
    },
  ].filter((item) => item.display)
}

/**
 * useUserNavigation constructs the user menu nav items for the primary application layout.
 * The user param is only expected to be a sapphire or emerald member user and does not
 * concern itself with staff users.
 *
 * @param user The current user
 * @returns ComputedRef<UserMenuItem[]>
 */
export function useUserNavigation(user: User) {
  const badgeCounts = computed(() => {
    if (user.canAccessMemberContent) {
      const { count: unreadMessages } = useAppMessagesCount()
      const { count: pendingTodos } = useAppTodosCount()

      return {
        pendingTodos: pendingTodos.value,
        unreadMessages: unreadMessages.value,
      }
    }

    return {
      pendingTodos: 0,
      unreadMessages: 0,
    }
  })

  return computed(() => {
    const navigation: Array<UserMenuItem> = [
      {
        action: "url",
        activityBadge: badgeCounts.value.pendingTodos,
        activityBadgeMax: 5,
        icon: ClipboardCheckIcon,
        name: badgeCounts.value.pendingTodos
          ? `${badgeCounts.value.pendingTodos} Pending To-Dos`
          : "To-Dos",
        show: user.canAccessMemberContent,
        url: router.Todos(),
      },
      {
        action: "url",
        activityBadge: badgeCounts.value.unreadMessages,
        activityBadgeMax: 5,
        icon: BellIcon,
        name: badgeCounts.value.unreadMessages
          ? `${badgeCounts.value.unreadMessages} Unread Announcements`
          : "Announcements",
        show: user.canAccessMemberContent,
        url: router.Notifications(),
      },
      {
        action: "nav",
        icon: UserCircleIcon,
        name: user.canAccessMemberContent ? "Profile and Settings" : "Settings",
        navigation: [
          {
            description:
              "Update your member profile for community and find an advisor.",
            name: "Member Profile",
            show: user.canAccessMemberContent,
            url: router.AdvisorProfiles(),
          },
          {
            description: user.canAccessMemberContent
              ? "Update your user and firm settings."
              : "",
            name: "Settings",
            show: true,
            url: router.Settings(),
          },
          {
            name: "Log Out",
            description: "",
            show: true,
            url: router.Logoff(),
          },
        ],
        show: true,
      },
    ]

    return navigation
  })
}

/**
 * Browser history methods
 */
export const history = {
  back() {
    window.history.back()
  },
}
